import { useRef, useState, useEffect } from "react";
import SignaturePad from "react-signature-canvas";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Checkbox } from "@/components/ui/checkbox";
import { PenLine, Trash2, Type } from "lucide-react";
import { toast } from "sonner";

export default function SignatureDialog({ open, onOpenChange, onConfirm, defaultName = "" }) {
    const padRef = useRef(null);
    const [typed, setTyped] = useState(defaultName);
    const [mode, setMode] = useState("draw");
    const [agreed, setAgreed] = useState(false);
    const [empty, setEmpty] = useState(true);

    useEffect(() => {
        if (open) {
            setTyped(defaultName);
            setAgreed(false);
            setEmpty(true);
            setTimeout(() => padRef.current?.clear(), 0);
        }
    }, [open, defaultName]);

    const clear = () => {
        padRef.current?.clear();
        setEmpty(true);
    };

    const renderTypedSignaturePng = (name) => {
        const canvas = document.createElement("canvas");
        canvas.width = 500; canvas.height = 140;
        const ctx = canvas.getContext("2d");
        ctx.fillStyle = "rgba(0,0,0,0)";
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "hsl(155 50% 18%)";
        ctx.font = "italic 64px 'Playfair Display', Georgia, serif";
        ctx.textBaseline = "middle";
        ctx.fillText(name, 16, canvas.height / 2);
        return canvas.toDataURL("image/png");
    };

    const toErrorString = (d) => {
        if (!d) return "Signing failed";
        if (typeof d === "string") return d;
        if (Array.isArray(d)) return d.map(x => x?.msg || JSON.stringify(x)).join("; ");
        if (typeof d === "object") return d.msg || d.detail || JSON.stringify(d);
        return String(d);
    };

    const confirm = async () => {
        if (!agreed) return toast.error("Please certify the information is accurate to continue.");
        let signature_data_url = null;
        const trimmedName = typed.trim();
        if (mode === "draw") {
            if (padRef.current?.isEmpty()) return toast.error("Please draw your signature.");
            signature_data_url = padRef.current.getCanvas().toDataURL("image/png");
        } else {
            if (!trimmedName) return toast.error("Please type your full legal name.");
            signature_data_url = renderTypedSignaturePng(trimmedName);
        }
        try {
            await onConfirm({ signature_data_url, typed_name: trimmedName || null });
        } catch (e) {
            toast.error(toErrorString(e?.response?.data?.detail || e?.message));
        }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg bg-card">
                <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">E-sign your packet</DialogTitle>
                    <DialogDescription>Your signature completes the packet. Everything is still submitted by you to the agency's portal — Owed never files on your behalf without it.</DialogDescription>
                </DialogHeader>

                <Tabs value={mode} onValueChange={setMode} className="mt-2">
                    <TabsList className="grid grid-cols-2 bg-secondary rounded-full h-10 p-1">
                        <TabsTrigger value="draw" className="rounded-full data-[state=active]:bg-foreground data-[state=active]:text-background"><PenLine className="h-3.5 w-3.5 mr-1" /> Draw</TabsTrigger>
                        <TabsTrigger value="type" className="rounded-full data-[state=active]:bg-foreground data-[state=active]:text-background"><Type className="h-3.5 w-3.5 mr-1" /> Type</TabsTrigger>
                    </TabsList>
                    <TabsContent value="draw" className="mt-4">
                        <div className="relative rounded-xl border border-dashed border-border bg-[linear-gradient(to_bottom,hsl(var(--background)),hsl(var(--secondary)))]">
                            <SignaturePad
                                ref={padRef}
                                canvasProps={{ className: "w-full h-40 block rounded-xl" }}
                                penColor="hsl(155 50% 18%)"
                                onEnd={() => setEmpty(false)}
                            />
                            {empty && (
                                <div className="absolute inset-0 flex items-center justify-center pointer-events-none text-muted-foreground text-sm font-serif italic">sign here</div>
                            )}
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                            <p className="text-xs text-muted-foreground">Draw with mouse, trackpad, or finger.</p>
                            <Button type="button" variant="ghost" size="sm" onClick={clear}><Trash2 className="h-3.5 w-3.5 mr-1" /> Clear</Button>
                        </div>
                        <div className="mt-3">
                            <Label className="text-xs text-muted-foreground">Printed legal name</Label>
                            <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Alex Morgan" className="mt-1 rounded-xl" />
                        </div>
                    </TabsContent>
                    <TabsContent value="type" className="mt-4">
                        <Label className="text-xs text-muted-foreground">Type your full legal name as your signature</Label>
                        <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder="Alex Taylor Morgan" className="mt-2 h-14 rounded-xl font-serif text-2xl italic" />
                        <p className="mt-2 text-xs text-muted-foreground">Typing your full name creates a legally binding e-signature per the E-SIGN Act.</p>
                    </TabsContent>
                </Tabs>

                <label className="mt-4 flex items-start gap-3 p-3 rounded-xl bg-secondary/50 cursor-pointer">
                    <Checkbox checked={agreed} onCheckedChange={(v) => setAgreed(!!v)} className="mt-0.5" />
                    <span className="text-xs leading-relaxed text-muted-foreground">I certify under penalty of perjury that the information in this packet is true and accurate to the best of my knowledge, and I intend this signature to be a legally binding e-signature.</span>
                </label>

                <DialogFooter className="mt-2">
                    <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
                    <Button onClick={confirm} className="rounded-full bg-foreground text-background hover:bg-foreground/90">Sign & finalize packet</Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
